/**
 * Local database (IndexedDB via Dexie).
 *
 * Schema v1: session, projects, members, outbox, media queue (stub), cursors.
 * Schema v2: Phase 1 field entities (stages, milestones, tasks, progress
 * updates, issues, diary entries, media metadata), the real media upload
 * queue, the cached dashboard and the notifications inbox.
 * Schema v3: Phase 2 workforce — engagements (project_worker), append-only
 * attendance events and the worker-days the server derives from them.
 * Schema v4: Phase 3 cost control — bills of quantities (read-only copies of
 * BOQs, versions, sections and items, so field users see quantities offline)
 * and expenses (recorded offline with a receipt photo).
 * Schema v5: Phase 4 procurement — requisitions (drafted and submitted
 * offline), purchase orders (read-only, so the site can receive without
 * signal) and deliveries (received offline with photos and a signature).
 * Schema v6: module 3 quality and safety — checklist templates, inspection
 * requests and inspections with append-only item results, NCRs and
 * rectifications, floor plans (plus a cache of their images for offline snag
 * walks) and snags; incidents, toolbox talks and append-only attendance.
 * Schema v7: module 4 site planning — zones (read-only), crews (read-only),
 * constraints (raised offline), daily plan lines, visitor log entries and
 * action items (completed offline by their owner).
 * Schema v8: module 5 equipment and inventory — stock items (read-only),
 * stock movements (issues and returns recorded offline; append-only),
 * material requests (raised, edited and cancelled offline), equipment
 * (read-only, with its pre-use checklist), equipment logs (editable) and
 * pre-use checks (append-only).
 * Schema v9: module 6 design management — drawings and drawing revisions
 * (read-only: the construction set on site; a superseded revision arrives
 * marked), specification sections (read-only), RFIs (raised and edited
 * offline), site visits and observations (recorded offline), and a cache of
 * the construction set's files so drawings open with no signal.
 * Schema v10: module 7 QS commercial — the surveyor's valuations (read-only)
 * and its measurement-sheet rows (recorded offline on site: independent rows,
 * edits merged per field, removal a tombstone).
 * Schema v11: module 8 subcontracts — the subcontractor's daily reports
 * (written and submitted offline: editable narrative merged per field; the
 * contractor's people receive them once submitted).
 * Schema v12: module 9 owner extras — the owner's warranty claims.
 * Schema v13: module 11 messaging — the conversations a member takes part in
 * (read-only) and their recent messages (the last 30 days, at most 200 per
 * conversation): append-only events written offline and sent in order on
 * reconnect, edited only within 15 minutes of sending.
 *
 * Rules for future versions: never edit an existing `version(n)`
 * block — add `version(n + 1)` with an upgrade function — and remember that a
 * field user may open the app after weeks offline with outbox rows written by
 * an old build, so outbox migrations must preserve every queued mutation.
 *
 * Components never touch Dexie directly; they go through the repositories in
 * this folder.
 */
import Dexie, { type DexieOptions, type Table } from 'dexie';

import type {
  AppNotification,
  Dashboard,
  DiaryEntry,
  Issue,
  MediaAsset,
  MediaEntityType,
  MediaKind,
  Milestone,
  ProgressUpdate,
  Stage,
  Task,
} from '@/lib/api/field-types';
import type { ConflictInfo, Me, MutationOperation, ProjectMembership } from '@/lib/api/types';
import type { AttendanceEvent, Engagement, WorkerDay } from '@/lib/api/workforce-types';
import type { Boq, BoqItem, BoqSection, BoqVersion, Expense } from '@/lib/api/cost-types';
import type { Delivery, PurchaseOrder, Requisition } from '@/lib/api/procurement-types';
import type {
  ChecklistTemplate,
  FloorPlan,
  Incident,
  Inspection,
  InspectionRequest,
  InspectionResult,
  Ncr,
  Rectification,
  Snag,
  ToolboxAttendance,
  ToolboxTalk,
} from '@/lib/api/quality-types';
import type {
  ActionItem,
  Constraint,
  Crew,
  DailyAssignment,
  Visit,
  Zone,
} from '@/lib/api/siteplan-types';
import type {
  Equipment,
  EquipmentCheck,
  EquipmentLog,
  MaterialRequest,
  StockItem,
  StockMovement,
} from '@/lib/api/resources-types';
import type {
  Drawing,
  DrawingRevision,
  Observation,
  Rfi,
  SiteVisit,
  SpecSection,
} from '@/lib/api/design-types';
import type { Measurement, Valuation } from '@/lib/api/commercial-types';
import type { WarrantyClaim } from '@/lib/api/owner-types';
import type { SubReport } from '@/lib/api/subcontracts-types';
import type { Conversation, Message } from '@/lib/api/messaging-types';

export type SessionRecord = {
  key: 'current';
  me: Me;
  cached_at: string;
};

export type LocalState = 'synced' | 'pending';

export type ProjectRecord = {
  id: string;
  organization_id: string;
  name: string;
  code: string | null;
  status: string;
  cover_url: string | null;
  /** Membership facts from /me (absent for projects only seen via sync). */
  membership: Pick<ProjectMembership, 'party_role' | 'role' | 'permissions'> | null;
  /** Latest server entity data from sync pull (shape owned by the API). */
  data: Record<string, unknown>;
  version: number | null;
  local_state: LocalState;
  updated_at: string;
};

export type ProjectMemberRecord = {
  id: string;
  project_id: string;
  user_id: string | null;
  data: Record<string, unknown>;
  version: number;
};

export type OutboxState = 'pending' | 'in_flight' | 'applied' | 'conflict' | 'rejected' | 'failed';

export type OutboxRecord = {
  mutation_id: string;
  entity_type: string;
  entity_id: string;
  operation: MutationOperation;
  base_version: number | null;
  payload: Record<string, unknown>;
  created_at_device: string;
  attempts: number;
  state: OutboxState;
  last_error: { code: string; message: string } | null;
  project_id: string | null;
  /** Epoch ms before which a `failed` record is not retried automatically. */
  next_attempt_at: number | null;
  conflict: ConflictInfo | null;
  /** Human label for the Sync Center ("Create project · Riverside"). */
  summary: string;
};

/** v1 shape of a media queue row (the stub never wrote any, but migrate anyway). */
export type MediaQueueRecordV1 = {
  id: string;
  project_id: string | null;
  entity_type: string;
  entity_id: string;
  blob: Blob | null;
  mime_type: string;
  size: number;
  state: 'pending' | 'uploading' | 'uploaded' | 'failed';
  attempts: number;
  created_at: string;
  last_error: string | null;
};

export type MediaQueueState = 'queued' | 'uploading' | 'uploaded' | 'failed';
/** How far an upload got, so a retry resumes instead of starting over. */
export type MediaUploadStep = 'init' | 'put' | 'complete' | 'done';

/**
 * A captured file waiting to upload. The bytes live here (IndexedDB) until
 * the server confirms them; the record it belongs to syncs through the
 * outbox independently, so a big photo never blocks a 200-byte diary edit.
 */
export type MediaQueueRecord = {
  /** The asset id, generated on the device (UUIDv7). */
  id: string;
  project_id: string;
  entity_type: MediaEntityType;
  entity_id: string;
  kind: MediaKind;
  /** Bytes to upload (compressed for photos); dropped once uploaded. */
  blob: Blob | null;
  /** Small local preview, kept after upload until the server copy is viewable. */
  thumbnail: Blob | null;
  mime_type: string;
  size: number;
  sha256: string;
  file_name: string;
  caption: string;
  captured_at_device: string;
  latitude: number | null;
  longitude: number | null;
  gps_accuracy_m: number | null;
  state: MediaQueueState;
  step: MediaUploadStep;
  attempts: number;
  next_attempt_at: number | null;
  /** The person asked to upload now despite the Wi-Fi-only preference. */
  force: boolean;
  created_at: string;
  last_error: string | null;
};

/**
 * A synced field entity. `server` is the last copy the server confirmed
 * (null for a record created on this device and not yet accepted); `data` is
 * what screens show — the server copy with queued local mutations applied.
 */
export type FieldRecord<T> = {
  id: string;
  project_id: string;
  data: T;
  server: T | null;
  version: number | null;
  local_state: LocalState;
  updated_at: string;
};

export type StageRecord = FieldRecord<Stage>;
export type MilestoneRecord = FieldRecord<Milestone>;
export type TaskRecord = FieldRecord<Task>;
export type ProgressUpdateRecord = FieldRecord<ProgressUpdate>;
export type IssueRecord = FieldRecord<Issue>;
export type DiaryEntryRecord = FieldRecord<DiaryEntry>;
export type MediaAssetRecord = FieldRecord<MediaAsset>;
export type EngagementRecord = FieldRecord<Engagement>;
export type AttendanceEventRecord = FieldRecord<AttendanceEvent>;
export type WorkerDayRecord = FieldRecord<WorkerDay>;
export type BoqRecord = FieldRecord<Boq>;
export type BoqVersionRecord = FieldRecord<BoqVersion>;
export type BoqSectionRecord = FieldRecord<BoqSection>;
export type BoqItemRecord = FieldRecord<BoqItem>;
export type ExpenseRecord = FieldRecord<Expense>;
export type RequisitionRecord = FieldRecord<Requisition>;
export type PurchaseOrderRecord = FieldRecord<PurchaseOrder>;
export type DeliveryRecord = FieldRecord<Delivery>;
export type ChecklistTemplateRecord = FieldRecord<ChecklistTemplate>;
export type InspectionRequestRecord = FieldRecord<InspectionRequest>;
export type InspectionRecord = FieldRecord<Inspection>;
export type InspectionResultRecord = FieldRecord<InspectionResult>;
export type NcrRecord = FieldRecord<Ncr>;
export type RectificationRecord = FieldRecord<Rectification>;
export type FloorPlanRecord = FieldRecord<FloorPlan>;
export type SnagRecord = FieldRecord<Snag>;
export type IncidentRecord = FieldRecord<Incident>;
export type ToolboxTalkRecord = FieldRecord<ToolboxTalk>;
export type ToolboxAttendanceRecord = FieldRecord<ToolboxAttendance>;
export type ZoneRecord = FieldRecord<Zone>;
export type CrewRecord = FieldRecord<Crew>;
export type ConstraintRecord = FieldRecord<Constraint>;
export type DailyAssignmentRecord = FieldRecord<DailyAssignment>;
export type VisitRecord = FieldRecord<Visit>;
export type ActionItemRecord = FieldRecord<ActionItem>;
export type StockItemRecord = FieldRecord<StockItem>;
export type StockMovementRecord = FieldRecord<StockMovement>;
export type MaterialRequestRecord = FieldRecord<MaterialRequest>;
export type EquipmentRecord = FieldRecord<Equipment>;
export type EquipmentLogRecord = FieldRecord<EquipmentLog>;
export type EquipmentCheckRecord = FieldRecord<EquipmentCheck>;
export type DrawingRecord = FieldRecord<Drawing>;
export type DrawingRevisionRecord = FieldRecord<DrawingRevision>;
export type SpecSectionRecord = FieldRecord<SpecSection>;
export type RfiRecord = FieldRecord<Rfi>;
export type SiteVisitRecord = FieldRecord<SiteVisit>;
export type ObservationRecord = FieldRecord<Observation>;
export type ValuationRecord = FieldRecord<Valuation>;
export type MeasurementRecord = FieldRecord<Measurement>;
export type SubReportRecord = FieldRecord<SubReport>;
export type WarrantyClaimRecord = FieldRecord<WarrantyClaim>;
export type ConversationRecord = FieldRecord<Conversation>;
export type MessageRecord = FieldRecord<Message>;

/** A construction-set drawing's file kept on the device (image or PDF) so it opens offline. */
export type DrawingFileRecord = {
  revision_id: string;
  drawing_id: string;
  project_id: string;
  asset_id: string;
  content_type: string;
  blob: Blob;
  fetched_at: string;
};

/** A floor plan's display image kept on the device so a snag walk works offline. */
export type PlanImageRecord = {
  plan_id: string;
  project_id: string;
  asset_id: string;
  blob: Blob;
  fetched_at: string;
};

export type DashboardRecord = { project_id: string; data: Dashboard; fetched_at: string };

export type NotificationRecord = AppNotification;

export type SyncCursorRecord = {
  project_id: string;
  cursor: string | null;
  updated_at: string;
};

export type KvRecord = { key: string; value: unknown };

export class BuildDatabase extends Dexie {
  session!: Table<SessionRecord, string>;
  projects!: Table<ProjectRecord, string>;
  project_members!: Table<ProjectMemberRecord, string>;
  outbox!: Table<OutboxRecord, string>;
  media_queue!: Table<MediaQueueRecord, string>;
  sync_cursors!: Table<SyncCursorRecord, string>;
  kv!: Table<KvRecord, string>;
  stages!: Table<StageRecord, string>;
  milestones!: Table<MilestoneRecord, string>;
  tasks!: Table<TaskRecord, string>;
  progress_updates!: Table<ProgressUpdateRecord, string>;
  issues!: Table<IssueRecord, string>;
  diary_entries!: Table<DiaryEntryRecord, string>;
  media_assets!: Table<MediaAssetRecord, string>;
  dashboards!: Table<DashboardRecord, string>;
  notifications!: Table<NotificationRecord, string>;
  project_workers!: Table<EngagementRecord, string>;
  attendance_events!: Table<AttendanceEventRecord, string>;
  worker_days!: Table<WorkerDayRecord, string>;
  boqs!: Table<BoqRecord, string>;
  boq_versions!: Table<BoqVersionRecord, string>;
  boq_sections!: Table<BoqSectionRecord, string>;
  boq_items!: Table<BoqItemRecord, string>;
  expenses!: Table<ExpenseRecord, string>;
  requisitions!: Table<RequisitionRecord, string>;
  purchase_orders!: Table<PurchaseOrderRecord, string>;
  deliveries!: Table<DeliveryRecord, string>;
  checklist_templates!: Table<ChecklistTemplateRecord, string>;
  inspection_requests!: Table<InspectionRequestRecord, string>;
  inspections!: Table<InspectionRecord, string>;
  inspection_results!: Table<InspectionResultRecord, string>;
  ncrs!: Table<NcrRecord, string>;
  rectifications!: Table<RectificationRecord, string>;
  floor_plans!: Table<FloorPlanRecord, string>;
  snags!: Table<SnagRecord, string>;
  incidents!: Table<IncidentRecord, string>;
  toolbox_talks!: Table<ToolboxTalkRecord, string>;
  toolbox_attendance!: Table<ToolboxAttendanceRecord, string>;
  plan_images!: Table<PlanImageRecord, string>;
  zones!: Table<ZoneRecord, string>;
  crews!: Table<CrewRecord, string>;
  constraints!: Table<ConstraintRecord, string>;
  daily_assignments!: Table<DailyAssignmentRecord, string>;
  visits!: Table<VisitRecord, string>;
  action_items!: Table<ActionItemRecord, string>;
  stock_items!: Table<StockItemRecord, string>;
  stock_movements!: Table<StockMovementRecord, string>;
  material_requests!: Table<MaterialRequestRecord, string>;
  equipment!: Table<EquipmentRecord, string>;
  equipment_logs!: Table<EquipmentLogRecord, string>;
  equipment_checks!: Table<EquipmentCheckRecord, string>;
  drawings!: Table<DrawingRecord, string>;
  drawing_revisions!: Table<DrawingRevisionRecord, string>;
  spec_sections!: Table<SpecSectionRecord, string>;
  rfis!: Table<RfiRecord, string>;
  site_visits!: Table<SiteVisitRecord, string>;
  observations!: Table<ObservationRecord, string>;
  drawing_files!: Table<DrawingFileRecord, string>;
  valuations!: Table<ValuationRecord, string>;
  measurements!: Table<MeasurementRecord, string>;
  sub_daily_reports!: Table<SubReportRecord, string>;
  warranty_claims!: Table<WarrantyClaimRecord, string>;
  conversations!: Table<ConversationRecord, string>;
  messages!: Table<MessageRecord, string>;

  constructor(name = 'koros-build', options?: DexieOptions) {
    super(name, options);
    this.version(1).stores({
      session: '&key',
      projects: '&id, organization_id, status, local_state',
      project_members: '&id, project_id, user_id',
      outbox: '&mutation_id, state, project_id, created_at_device, [entity_type+entity_id]',
      media_queue: '&id, state, project_id, created_at',
      sync_cursors: '&project_id',
      kv: '&key',
    });

    // v2 — Phase 1 field operations. Additive: no v1 table changes shape
    // except media_queue, whose rows are carried over into the new layout.
    this.version(2)
      .stores({
        media_queue: '&id, state, project_id, created_at, [entity_type+entity_id]',
        stages: '&id, project_id',
        milestones: '&id, project_id',
        tasks: '&id, project_id',
        progress_updates: '&id, project_id, [project_id+data.stage_id]',
        issues: '&id, project_id',
        diary_entries: '&id, project_id, [project_id+data.entry_date]',
        media_assets: '&id, project_id, [data.entity_type+data.entity_id]',
        dashboards: '&project_id',
        notifications: '&id, created_at',
      })
      .upgrade(async (tx) => {
        // A v1 build skipped stage/task/diary/… changes it could not store and
        // still advanced its cursors past them. Forget the cursors so the next
        // pull backfills every entity type from the start.
        await tx.table('sync_cursors').clear();
        await tx
          .table('media_queue')
          .toCollection()
          .modify((row: Partial<MediaQueueRecord> & Partial<MediaQueueRecordV1>) => {
            const v1State = row.state as MediaQueueRecordV1['state'] | undefined;
            Object.assign(row, {
              project_id: row.project_id ?? '',
              kind: row.kind ?? (row.mime_type?.startsWith('video/') ? 'video' : 'photo'),
              thumbnail: row.thumbnail ?? null,
              sha256: row.sha256 ?? '',
              file_name: row.file_name ?? '',
              caption: row.caption ?? '',
              captured_at_device: row.captured_at_device ?? row.created_at,
              latitude: row.latitude ?? null,
              longitude: row.longitude ?? null,
              gps_accuracy_m: row.gps_accuracy_m ?? null,
              state: v1State === 'pending' ? 'queued' : (v1State ?? 'queued'),
              step: row.step ?? (v1State === 'uploaded' ? 'done' : 'init'),
              next_attempt_at: row.next_attempt_at ?? null,
              force: row.force ?? false,
            });
          });
      });

    // v3 — Phase 2 workforce. Additive: nothing existing changes shape, and
    // every queued mutation is kept as it is. A v2 build skipped these entity
    // types in pulls and still advanced its cursors past them, so the cursors
    // are reset once to backfill the roster, attendance and worker-days.
    this.version(3)
      .stores({
        project_workers: '&id, project_id',
        attendance_events: '&id, project_id, [project_id+data.work_date], data.voids_id',
        worker_days: '&id, project_id, [project_id+data.work_date], data.engagement_id',
      })
      .upgrade(async (tx) => {
        await tx.table('sync_cursors').clear();
      });

    // v4 — Phase 3 cost control. Additive, like v3: nothing existing changes
    // shape and every queued mutation is kept. The cursors are reset once so
    // the BOQs and expenses a v3 build skipped are backfilled.
    this.version(4)
      .stores({
        boqs: '&id, project_id',
        boq_versions: '&id, project_id, data.boq_id',
        boq_sections: '&id, project_id, data.version_id',
        boq_items: '&id, project_id, data.version_id, data.lineage',
        expenses: '&id, project_id',
      })
      .upgrade(async (tx) => {
        await tx.table('sync_cursors').clear();
      });

    // v5 — Phase 4 procurement. Additive, like v3 and v4: nothing existing
    // changes shape and every queued mutation is kept. The cursors are reset
    // once so the requisitions, orders and deliveries a v4 build skipped are
    // backfilled.
    this.version(5)
      .stores({
        requisitions: '&id, project_id',
        purchase_orders: '&id, project_id',
        deliveries: '&id, project_id, data.purchase_order_id',
      })
      .upgrade(async (tx) => {
        await tx.table('sync_cursors').clear();
      });

    // v6 — module 3 quality and safety. Additive, like v3–v5: nothing existing
    // changes shape and every queued mutation is kept. The cursors are reset
    // once so the quality and safety records a v5 build skipped are backfilled.
    this.version(6)
      .stores({
        checklist_templates: '&id, project_id',
        inspection_requests: '&id, project_id',
        inspections: '&id, project_id, data.request_id',
        inspection_results: '&id, project_id, data.inspection_id',
        ncrs: '&id, project_id',
        rectifications: '&id, project_id, data.ncr_id, data.snag_id',
        floor_plans: '&id, project_id',
        snags: '&id, project_id, data.plan_id',
        incidents: '&id, project_id',
        toolbox_talks: '&id, project_id',
        toolbox_attendance: '&id, project_id, data.talk_id',
        plan_images: '&plan_id, project_id',
      })
      .upgrade(async (tx) => {
        await tx.table('sync_cursors').clear();
      });

    // v7 — module 4 site planning. Additive, like v3–v6: nothing existing
    // changes shape and every queued mutation (task edits included, whose
    // payloads may now carry zone, crew and predecessors) is kept. The cursors
    // are reset once so the zones, crews, constraints, daily plan lines,
    // visitors and actions a v6 build skipped are backfilled.
    this.version(7)
      .stores({
        zones: '&id, project_id',
        crews: '&id, project_id',
        constraints: '&id, project_id, data.task_id',
        daily_assignments: '&id, project_id, [project_id+data.plan_date]',
        visits: '&id, project_id, [project_id+data.visit_date]',
        action_items: '&id, project_id, data.meeting_id',
      })
      .upgrade(async (tx) => {
        await tx.table('sync_cursors').clear();
      });

    // v8 — module 5 equipment and inventory. Additive, like v3–v7: nothing
    // existing changes shape and every queued mutation is kept. The cursors
    // are reset once so the stock, requests and plant a v7 build skipped are
    // backfilled.
    this.version(8)
      .stores({
        stock_items: '&id, project_id',
        stock_movements: '&id, project_id, data.item_id, data.material_request_id',
        material_requests: '&id, project_id',
        equipment: '&id, project_id',
        equipment_logs: '&id, project_id, data.equipment_id',
        equipment_checks: '&id, project_id, data.equipment_id',
      })
      .upgrade(async (tx) => {
        await tx.table('sync_cursors').clear();
      });

    // v9 — module 6 design management. Additive, like v3–v8: nothing existing
    // changes shape and every queued mutation is kept. The cursors are reset
    // once so the drawings, specification, RFIs and site visits a v8 build
    // skipped are backfilled.
    this.version(9)
      .stores({
        drawings: '&id, project_id',
        drawing_revisions: '&id, project_id, data.drawing_id',
        spec_sections: '&id, project_id',
        rfis: '&id, project_id',
        site_visits: '&id, project_id',
        observations: '&id, project_id, data.visit_id',
        drawing_files: '&revision_id, project_id, drawing_id',
      })
      .upgrade(async (tx) => {
        await tx.table('sync_cursors').clear();
      });

    // v10 — module 7 QS commercial. Additive, like v3–v9: nothing existing
    // changes shape and every queued mutation is kept. The cursors are reset
    // once so the valuations and measurement rows a v9 build skipped are
    // backfilled.
    this.version(10)
      .stores({
        valuations: '&id, project_id',
        measurements: '&id, project_id, data.valuation_id',
      })
      .upgrade(async (tx) => {
        await tx.table('sync_cursors').clear();
      });

    // v11 — module 8 subcontracts. Additive, like v3–v10: nothing existing
    // changes shape and every queued mutation is kept. The cursors are reset
    // once so the daily reports a v10 build skipped are backfilled.
    this.version(11)
      .stores({
        sub_daily_reports: '&id, project_id, [project_id+data.report_date]',
      })
      .upgrade(async (tx) => {
        await tx.table('sync_cursors').clear();
      });

    // v12 — module 9 owner extras: the owner's warranty claims (raised on a
    // walk-through with no signal). Additive like v3–v11: nothing existing
    // changes shape and every queued mutation is kept; the cursors are reset
    // once so the claims a v11 build skipped are backfilled.
    this.version(12)
      .stores({
        warranty_claims: '&id, project_id',
      })
      .upgrade(async (tx) => {
        await tx.table('sync_cursors').clear();
      });

    // v13 — module 11 messaging: conversations (read-only) and their recent
    // messages, so a site supervisor reads the latest threads with no signal
    // and writes messages that go on reconnect. Additive like v3–v12: nothing
    // existing changes shape and every queued mutation is kept; the cursors
    // are reset once so the conversations a v12 build skipped are backfilled.
    this.version(13)
      .stores({
        conversations: '&id, project_id',
        messages: '&id, project_id, data.conversation_id',
      })
      .upgrade(async (tx) => {
        await tx.table('sync_cursors').clear();
      });
  }
}

/**
 * The local database. A live binding: a staff "view as" session (read-only,
 * `lib/auth/view-as.ts`) swaps it for an in-memory one before anything of the
 * person's is loaded, so none of it is written to this browser's IndexedDB.
 * Never capture `db` (or a table of it) in a module-level constant.
 */
export let db = new BuildDatabase();

/** The IndexedDB implementation a memory-only database runs on (fake-indexeddb). */
export type MemoryBackend = { indexedDB: IDBFactory; IDBKeyRange: typeof IDBKeyRange };

let inMemory = false;

/**
 * Staff "view as": from now on this tab keeps everything in memory only. The real
 * database (the staff member's own, if they use the app here) is left untouched.
 * The backend is passed in so its code loads only on the view-as route.
 */
export function switchToMemoryDatabase(backend: MemoryBackend): void {
  if (inMemory) return;
  db = new BuildDatabase('koros-build-view-as', {
    indexedDB: backend.indexedDB,
    IDBKeyRange: backend.IDBKeyRange,
  });
  inMemory = true;
}

export function isMemoryDatabase(): boolean {
  return inMemory;
}

/** Every table that holds a signed-in person's data (all of them, today). */
export function protectedTables(): Table[] {
  return [
    db.session,
    db.projects,
    db.project_members,
    db.outbox,
    db.media_queue,
    db.sync_cursors,
    db.kv,
    db.stages,
    db.milestones,
    db.tasks,
    db.progress_updates,
    db.issues,
    db.diary_entries,
    db.media_assets,
    db.dashboards,
    db.notifications,
    db.project_workers,
    db.attendance_events,
    db.worker_days,
    db.boqs,
    db.boq_versions,
    db.boq_sections,
    db.boq_items,
    db.expenses,
    db.requisitions,
    db.purchase_orders,
    db.deliveries,
    db.checklist_templates,
    db.inspection_requests,
    db.inspections,
    db.inspection_results,
    db.ncrs,
    db.rectifications,
    db.floor_plans,
    db.snags,
    db.incidents,
    db.toolbox_talks,
    db.toolbox_attendance,
    db.plan_images,
    db.zones,
    db.crews,
    db.constraints,
    db.daily_assignments,
    db.visits,
    db.action_items,
    db.stock_items,
    db.stock_movements,
    db.material_requests,
    db.equipment,
    db.equipment_logs,
    db.equipment_checks,
    db.drawings,
    db.drawing_revisions,
    db.spec_sections,
    db.rfis,
    db.site_visits,
    db.observations,
    db.drawing_files,
    db.valuations,
    db.measurements,
    db.sub_daily_reports,
    db.warranty_claims,
    db.conversations,
    db.messages,
  ];
}

/** Wipe everything belonging to the signed-in person (logout, user switch). */
export async function clearProtectedData(): Promise<void> {
  const tables = protectedTables();
  await db.transaction('rw', tables, async () => {
    await Promise.all(tables.map((table) => table.clear()));
  });
}
